<script setup>
  import { defineProps, ref, watch } from 'vue'
  import { useRouter, useRoute } from 'vue-router'
  import { useSmoothScroll } from '../composables/useSmoothScroll'
  import { useAuth } from '../composables/useAuth'
  import { firebase } from '../firebase'
  import { useLogger } from '../composables/useLogger'

  defineProps({
    isScrolled: {
      type: Boolean,
      required: true,
    },
    toggleMobileMenu: {
      type: Function,
      required: true,
    },
  })

  const router = useRouter()
  const route = useRoute()
  const { scrollToElement } = useSmoothScroll()
  const { user, loading } = useAuth()
  const logger = useLogger()

  // Estado reactivo para la visibilidad
  const showHeader = ref(true)
  const isHomePage = ref(false)
  const isSecondaryPage = ref(false)
  const isBackofficePage = ref(false)
  const isMenuOpen = ref(false)

  // Función para actualizar el estado
  const updateRouteState = () => {
    isHomePage.value = route.path === '/'
    isSecondaryPage.value = route.path !== '/'
    isBackofficePage.value =
      route.path.startsWith('/backoffice/') && route.path !== '/backoffice/login'
    showHeader.value = true

    logger.info('Actualizando estado de la ruta', {
      component: 'Header',
      method: 'updateRouteState',
      data: {
        path: route.path,
        isHomePage: isHomePage.value,
        isSecondaryPage: isSecondaryPage.value,
        isBackofficePage: isBackofficePage.value,
        showHeader: showHeader.value,
      },
    })
  }

  // Observar cambios en la ruta
  watch(
    () => route.path,
    () => {
      updateRouteState()
    },
    { immediate: true },
  )

  const scrollToSection = sectionId => {
    isMenuOpen.value = false
    if (route.path.match(/^\/(jobs|backoffice)\//)) {
      router.push('/').then(() => {
        setTimeout(() => {
          scrollToElement(`#${sectionId}`)
        }, 100)
      })
    } else {
      scrollToElement(`#${sectionId}`)
    }
  }

  const handleAuthAction = () => {
    if (user.value) {
      router.push('/backoffice/dashboard')
    } else {
      router.push('/backoffice/login')
    }
  }

  const handleLogout = async () => {
    try {
      await firebase.auth().signOut()
      logger.info('Sesión cerrada exitosamente', { component: 'Header', method: 'handleLogout' })
      router.push('/backoffice/login')
    } catch (error) {
      logger.handleError(error, { component: 'Header', method: 'handleLogout' })
    }
  }

  const toggleMenu = () => {
    isMenuOpen.value = !isMenuOpen.value
  }
</script>

<template>
  <header
v-if="showHeader"
:class="[
      'fixed top-0 left-0 right-0 z-50 transition-all duration-300',
      isScrolled ? 'bg-white/80 backdrop-blur-md shadow-sm' : 'bg-transparent',
    ]">
    <div class="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
      <div class="flex justify-between items-center h-20">
        <!-- Logo y nombre -->
        <div class="flex items-center gap-3">
          <img src="/assets/accyon.png" alt="Accyon Logo" class="w-8 h-8" />
          <h1 class="text-2xl font-serif text-gray-800">ACCYON</h1>
        </div>

        <!-- Navegación principal -->
        <nav class="hidden md:flex items-center gap-8">
          <!-- Menú de navegación para la página principal -->
          <ul v-if="isHomePage" class="flex items-center gap-8">
            <li>
              <a
href="#section-hero"
                class="text-gray-600 hover:text-teal-600 transition-colors cursor-pointer font-medium"
                @click.prevent="scrollToSection('section-hero')">
                Inicio
              </a>
            </li>
            <li>
              <a
href="#section-facts"
                class="text-gray-600 hover:text-teal-600 transition-colors cursor-pointer font-medium"
                @click.prevent="scrollToSection('section-facts')">
                Nosotros
              </a>
            </li>
            <li>
              <a
href="#section-services"
                class="text-gray-600 hover:text-teal-600 transition-colors cursor-pointer font-medium"
                @click.prevent="scrollToSection('section-services')">
                Servicios
              </a>
            </li>
            <li>
              <a
href="#section-process"
                class="text-gray-600 hover:text-teal-600 transition-colors cursor-pointer font-medium"
                @click.prevent="scrollToSection('section-process')">
                Proceso
              </a>
            </li>
            <li>
              <a
href="#section-testimonials"
                class="text-gray-600 hover:text-teal-600 transition-colors cursor-pointer font-medium"
                @click.prevent="scrollToSection('section-testimonials')">
                Testimonios
              </a>
            </li>
            <li>
              <a
href="#section-jobs"
                class="text-gray-600 hover:text-teal-600 transition-colors cursor-pointer font-medium"
                @click.prevent="scrollToSection('section-jobs')">
                Empleos
              </a>
            </li>
            <li>
              <a
href="#section-contact"
                class="text-gray-600 hover:text-teal-600 transition-colors cursor-pointer font-medium"
                @click.prevent="scrollToSection('section-contact')">
                Contacto
              </a>
            </li>
          </ul>

          <!-- Navegación del backoffice -->
          <div v-else-if="isBackofficePage" class="flex items-center space-x-4">
            <!-- Dashboard -->
            <router-link
to="/backoffice/dashboard"
              class="group relative flex items-center gap-2 border-transparent text-gray-500 hover:border-gray-300 hover:text-gray-700 px-1 pt-1 border-b-2 text-sm font-medium transition-all duration-200"
              :class="{ 'border-teal-500 text-teal-600': route.path === '/backoffice/dashboard' }">
              <svg
class="w-5 h-5"
fill="none"
viewBox="0 0 24 24"
stroke="currentColor">
                <path
stroke-linecap="round"
stroke-linejoin="round"
stroke-width="2"
                  d="M4 6a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2H6a2 2 0 01-2-2V6zM14 6a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2h-2a2 2 0 01-2-2V6zM4 16a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2H6a2 2 0 01-2-2v-2zM14 16a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2h-2a2 2 0 01-2-2v-2z" />
              </svg>
              <span>Dashboard</span>
            </router-link>

            <!-- Contacto -->
            <router-link
to="/backoffice/contacts"
              class="group relative flex items-center gap-2 border-transparent text-gray-500 hover:border-gray-300 hover:text-gray-700 px-1 pt-1 border-b-2 text-sm font-medium transition-all duration-200"
              :class="{ 'border-teal-500 text-teal-600': route.path === '/backoffice/contacts' }">
              <svg
xmlns="http://www.w3.org/2000/svg"
class="w-5 h-5"
fill="none"
viewBox="0 0 24 24"
                stroke="currentColor"
stroke-width="1"
stroke-linecap="round"
stroke-linejoin="round">
                <path d="M20 6v12a2 2 0 0 1 -2 2h-10a2 2 0 0 1 -2 -2v-12a2 2 0 0 1 2 -2h10a2 2 0 0 1 2 2z" />
                <path d="M10 16h6" />
                <path d="M13 11m-2 0a2 2 0 1 0 4 0a2 2 0 1 0 -4 0" />
                <path d="M4 8h3" />
                <path d="M4 12h3" />
                <path d="M4 16h3" />
              </svg>
              <span>Contacto</span>
            </router-link>

            <!-- Grupo de Vacantes -->
            <div class="backoffice-dropdown">
              <button
                class="group relative flex items-center gap-2 border-transparent text-gray-500 hover:border-gray-300 hover:text-gray-700 px-1 pt-1 border-b-2 text-sm font-medium transition-all duration-200 focus:outline-none"
                :class="{
                  'border-teal-500 text-teal-600': [
                    '/backoffice/jobs',
                    '/backoffice/applicants',
                    '/backoffice/job-board',
                  ].includes(route.path),
                }"
tabindex="0"
type="button"
aria-haspopup="true"
aria-expanded="false">
                <svg
xmlns="http://www.w3.org/2000/svg"
class="w-5 h-5"
viewBox="0 0 24 24"
fill="none"
                  stroke="currentColor"
stroke-width="1"
stroke-linecap="round"
stroke-linejoin="round">
                  <path d="M3 7m0 2a2 2 0 0 1 2 -2h14a2 2 0 0 1 2 2v9a2 2 0 0 1 -2 2h-14a2 2 0 0 1 -2 -2z" />
                  <path d="M8 7v-2a2 2 0 0 1 2 -2h4a2 2 0 0 1 2 2v2" />
                  <path d="M12 12l0 .01" />
                  <path d="M3 13a20 20 0 0 0 18 0" />
                </svg>
                <span>Vacantes</span>
                <svg
class="w-4 h-4 ml-1"
fill="none"
viewBox="0 0 24 24"
stroke="currentColor">
                  <path
stroke-linecap="round"
stroke-linejoin="round"
stroke-width="2"
d="M19 9l-7 7-7-7" />
                </svg>
              </button>
              <div class="dropdown-menu">
                <div class="py-1">
                  <router-link
to="/backoffice/jobs"
                    class="group flex items-center px-4 py-2 text-sm text-gray-700 hover:bg-gray-100">
                    <svg
class="w-5 h-5 mr-3"
fill="none"
viewBox="0 0 24 24"
stroke="currentColor">
                      <path
stroke-linecap="round"
stroke-linejoin="round"
stroke-width="2"
                        d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2" />
                    </svg>
                    Gestionar Vacantes
                  </router-link>
                  <router-link
to="/backoffice/applicants"
                    class="group flex items-center px-4 py-2 text-sm text-gray-700 hover:bg-gray-100">
                    <svg
class="w-5 h-5 mr-3"
fill="none"
viewBox="0 0 24 24"
stroke="currentColor">
                      <path
stroke-linecap="round"
stroke-linejoin="round"
stroke-width="2"
                        d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
                    </svg>
                    Solicitantes
                  </router-link>
                  <router-link
to="/backoffice/job-board"
                    class="group flex items-center px-4 py-2 text-sm text-gray-700 hover:bg-gray-100">
                    <svg
class="w-5 h-5 mr-3"
fill="none"
viewBox="0 0 24 24"
stroke="currentColor">
                      <path
stroke-linecap="round"
stroke-linejoin="round"
stroke-width="2"
                        d="M19 20H5a2 2 0 01-2-2V6a2 2 0 012-2h10a2 2 0 012 2v1m2 13a2 2 0 01-2-2V7m2 13a2 2 0 002-2V9a2 2 0 00-2-2h-2m-4-3H9M7 16h6M7 8h6v4H7V8z" />
                    </svg>
                    Bolsa de Trabajo
                  </router-link>
                </div>
              </div>
            </div>

            <!-- Grupo de Análisis -->
            <div class="backoffice-dropdown">
              <button
                class="group relative flex items-center gap-2 border-transparent text-gray-500 hover:border-gray-300 hover:text-gray-700 px-1 pt-1 border-b-2 text-sm font-medium transition-all duration-200 focus:outline-none"
                :class="{
                  'border-teal-500 text-teal-600': [
                    '/backoffice/objectives',
                    '/backoffice/audit-log',
                  ].includes(route.path),
                }"
tabindex="0"
type="button"
aria-haspopup="true"
aria-expanded="false">
                <svg
class="w-5 h-5"
fill="none"
viewBox="0 0 24 24"
stroke="currentColor">
                  <path
stroke-linecap="round"
stroke-linejoin="round"
stroke-width="2"
                    d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
                </svg>
                <span>Análisis</span>
                <svg
class="w-4 h-4 ml-1"
fill="none"
viewBox="0 0 24 24"
stroke="currentColor">
                  <path
stroke-linecap="round"
stroke-linejoin="round"
stroke-width="2"
d="M19 9l-7 7-7-7" />
                </svg>
              </button>
              <div class="dropdown-menu">
                <div class="py-1">
                  <router-link
to="/backoffice/audit-log"
                    class="group flex items-center px-4 py-2 text-sm text-gray-700 hover:bg-gray-100">
                    <svg
class="w-5 h-5 mr-3"
fill="none"
viewBox="0 0 24 24"
stroke="currentColor">
                      <path
stroke-linecap="round"
stroke-linejoin="round"
stroke-width="2"
                        d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-3 7h3m-3 4h3m-6-4h.01M9 16h.01" />
                    </svg>
                    Registro de Auditoría
                  </router-link>
                </div>
              </div>
            </div>

            <!-- Recursos -->
            <div class="backoffice-dropdown">
              <button
                class="group relative flex items-center gap-2 border-transparent text-gray-500 hover:border-gray-300 hover:text-gray-700 px-1 pt-1 border-b-2 text-sm font-medium transition-all duration-200 focus:outline-none"
                :class="{
                  'border-teal-500 text-teal-600': ['/backoffice/images'].includes(route.path),
                }"
tabindex="0"
type="button"
aria-haspopup="true"
aria-expanded="false">
                <svg
class="w-5 h-5"
fill="none"
viewBox="0 0 24 24"
stroke="currentColor">
                  <path
stroke-linecap="round"
stroke-linejoin="round"
stroke-width="2"
                    d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
                </svg>
                <span>Recursos</span>
                <svg
class="w-4 h-4 ml-1"
fill="none"
viewBox="0 0 24 24"
stroke="currentColor">
                  <path
stroke-linecap="round"
stroke-linejoin="round"
stroke-width="2"
d="M19 9l-7 7-7-7" />
                </svg>
              </button>
              <div class="dropdown-menu">
                <div class="py-1">
                  <router-link
to="/backoffice/images"
                    class="group flex items-center px-4 py-2 text-sm text-gray-700 hover:bg-gray-100">
                    <svg
class="w-5 h-5 mr-3"
fill="none"
viewBox="0 0 24 24"
stroke="currentColor">
                      <path
stroke-linecap="round"
stroke-linejoin="round"
stroke-width="2"
                        d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
                    </svg>
                    Gestionar Imágenes
                  </router-link>
                  <router-link to="/" class="group flex items-center px-4 py-2 text-sm text-gray-700 hover:bg-gray-100">
                    <svg
class="w-5 h-5 mr-3"
fill="none"
viewBox="0 0 24 24"
stroke="currentColor">
                      <path
stroke-linecap="round"
stroke-linejoin="round"
stroke-width="2"
                        d="M3 12l2-2m0 0l7-7 7 7M5 10v10a1 1 0 001 1h3m10-11l2 2m-2-2v10a1 1 0 01-1 1h-3m-6 0a1 1 0 001-1v-4a1 1 0 011-1h2a1 1 0 011 1v4a1 1 0 001 1m-6 0h6" />
                    </svg>
                    Sitio Web
                  </router-link>
                </div>
              </div>
            </div>
          </div>

          <!-- Botón de retorno para páginas secundarias -->
          <router-link
v-if="isSecondaryPage && !isBackofficePage"
to="/"
            class="border-transparent text-gray-500 hover:bg-gray-50 hover:border-gray-300 hover:text-gray-700 block pl-3 pr-4 py-2 border-l-4 text-base font-medium"
            @click="toggleMenu">
            Volver al inicio
          </router-link>

          <!-- Botón de autenticación/cierre de sesión -->
          <div class="ml-4">
            <button
v-if="!loading && !isBackofficePage"
:class="[
                'px-4 py-2 rounded-md transition-all duration-300 font-medium',
                user
                  ? 'bg-gradient-to-br from-teal-600 to-teal-500 text-white hover:shadow-md hover:from-teal-700 hover:to-teal-600'
                  : 'border-2 border-teal-600 text-teal-600 hover:bg-teal-600 hover:text-white',
              ]"
@click="handleAuthAction">
              {{ user ? 'Dashboard' : 'Entrar' }}
            </button>
            <button
v-else-if="!loading && isBackofficePage"
              class="bg-white p-1 rounded-full text-gray-400 hover:text-gray-500 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-teal-500"
              @click="handleLogout">
              <span class="sr-only">Cerrar sesión</span>
              <svg
class="h-6 w-6"
fill="none"
viewBox="0 0 24 24"
stroke="currentColor">
                <path
stroke-linecap="round"
stroke-linejoin="round"
stroke-width="2"
                  d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1" />
              </svg>
            </button>
            <div v-else class="w-8 h-8">
              <div class="animate-spin rounded-full h-8 w-8 border-b-2 border-teal-500"></div>
            </div>
          </div>
        </nav>

        <!-- Botón de menú móvil -->
        <button class="md:hidden p-2 rounded-md hover:bg-gray-100 transition-colors" @click="toggleMenu">
          <svg
xmlns="http://www.w3.org/2000/svg"
class="h-6 w-6 text-gray-600"
fill="none"
viewBox="0 0 24 24"
            stroke="currentColor">
            <path
stroke-linecap="round"
stroke-linejoin="round"
stroke-width="2"
d="M4 6h16M4 12h16M4 18h16" />
          </svg>
        </button>
      </div>
    </div>

    <!-- Menú móvil -->
    <div v-show="isMenuOpen" class="md:hidden bg-white">
      <div class="pt-2 pb-3 space-y-1">
        <!-- Menú móvil para página principal -->
        <template v-if="isHomePage">
          <a
href="#section-hero"
            class="border-transparent text-gray-500 hover:bg-gray-50 hover:border-gray-300 hover:text-gray-700 block pl-3 pr-4 py-2 border-l-4 text-base font-medium"
            @click.prevent="scrollToSection('section-hero')">
            Inicio
          </a>
          <a
href="#section-facts"
            class="border-transparent text-gray-500 hover:bg-gray-50 hover:border-gray-300 hover:text-gray-700 block pl-3 pr-4 py-2 border-l-4 text-base font-medium"
            @click.prevent="scrollToSection('section-facts')">
            Nosotros
          </a>
          <a
href="#section-services"
            class="border-transparent text-gray-500 hover:bg-gray-50 hover:border-gray-300 hover:text-gray-700 block pl-3 pr-4 py-2 border-l-4 text-base font-medium"
            @click.prevent="scrollToSection('section-services')">
            Servicios
          </a>
          <a
href="#section-process"
            class="border-transparent text-gray-500 hover:bg-gray-50 hover:border-gray-300 hover:text-gray-700 block pl-3 pr-4 py-2 border-l-4 text-base font-medium"
            @click.prevent="scrollToSection('section-process')">
            Proceso
          </a>
          <a
href="#section-testimonials"
            class="border-transparent text-gray-500 hover:bg-gray-50 hover:border-gray-300 hover:text-gray-700 block pl-3 pr-4 py-2 border-l-4 text-base font-medium"
            @click.prevent="scrollToSection('section-testimonials')">
            Testimonios
          </a>
          <a
href="#section-jobs"
            class="border-transparent text-gray-500 hover:bg-gray-50 hover:border-gray-300 hover:text-gray-700 block pl-3 pr-4 py-2 border-l-4 text-base font-medium"
            @click.prevent="scrollToSection('section-jobs')">
            Empleos
          </a>
          <a
href="#section-contact"
            class="border-transparent text-gray-500 hover:bg-gray-50 hover:border-gray-300 hover:text-gray-700 block pl-3 pr-4 py-2 border-l-4 text-base font-medium"
            @click.prevent="scrollToSection('section-contact')">
            Contacto
          </a>
        </template>

        <!-- Menú móvil para backoffice -->
        <template v-else-if="isBackofficePage">
          <router-link
to="/backoffice/dashboard"
            class="border-transparent text-gray-500 hover:bg-gray-50 hover:border-gray-300 hover:text-gray-700 block pl-3 pr-4 py-2 border-l-4 text-base font-medium">
            Dashboard
          </router-link>
          <router-link
to="/backoffice/jobs"
            class="border-transparent text-gray-500 hover:bg-gray-50 hover:border-gray-300 hover:text-gray-700 block pl-3 pr-4 py-2 border-l-4 text-base font-medium">
            Administrar Vacantes
          </router-link>
          <router-link
to="/backoffice/applicants"
            class="border-transparent text-gray-500 hover:bg-gray-50 hover:border-gray-300 hover:text-gray-700 block pl-3 pr-4 py-2 border-l-4 text-base font-medium">
            Solicitantes
          </router-link>
          <router-link
to="/backoffice/job-board"
            class="border-transparent text-gray-500 hover:bg-gray-50 hover:border-gray-300 hover:text-gray-700 block pl-3 pr-4 py-2 border-l-4 text-base font-medium">
            Bolsa de Trabajo
          </router-link>
          <router-link
to="/backoffice/objectives"
            class="border-transparent text-gray-500 hover:bg-gray-50 hover:border-gray-300 hover:text-gray-700 block pl-3 pr-4 py-2 border-l-4 text-base font-medium">
            Objetivos
          </router-link>
          <router-link
to="/backoffice/audit-log"
            class="border-transparent text-gray-500 hover:bg-gray-50 hover:border-gray-300 hover:text-gray-700 block pl-3 pr-4 py-2 border-l-4 text-base font-medium">
            Auditoría
          </router-link>
          <router-link
to="/backoffice/images"
            class="border-transparent text-gray-500 hover:bg-gray-50 hover:border-gray-300 hover:text-gray-700 block pl-3 pr-4 py-2 border-l-4 text-base font-medium">
            Imágenes
          </router-link>
          <router-link
to="/"
            class="border-transparent text-gray-500 hover:bg-gray-50 hover:border-gray-300 hover:text-gray-700 block pl-3 pr-4 py-2 border-l-4 text-base font-medium">
            Volver al sitio
          </router-link>
        </template>

        <!-- Botón de retorno para páginas secundarias -->
        <router-link
v-else-if="isSecondaryPage"
to="/"
          class="border-transparent text-gray-500 hover:bg-gray-50 hover:border-gray-300 hover:text-gray-700 block pl-3 pr-4 py-2 border-l-4 text-base font-medium"
          @click="toggleMenu">
          Volver al inicio
        </router-link>
      </div>
      <div class="pt-4 pb-3 border-t border-gray-200">
        <div class="flex items-center px-4">
          <button
v-if="!loading && !isBackofficePage"
:class="[
              'w-full px-4 py-2 rounded-md transition-all duration-300 font-medium',
              user
                ? 'bg-gradient-to-br from-teal-600 to-teal-500 text-white hover:shadow-md hover:from-teal-700 hover:to-teal-600'
                : 'border-2 border-teal-600 text-teal-600 hover:bg-teal-600 hover:text-white',
            ]"
@click="handleAuthAction">
            {{ user ? 'Dashboard' : 'Entrar' }}
          </button>
          <button
v-else-if="!loading && isBackofficePage"
            class="w-full px-4 py-2 text-red-600 hover:text-red-900 text-sm font-medium"
@click="handleLogout">
            Cerrar sesión
          </button>
        </div>
      </div>
    </div>
  </header>
</template>

<style scoped>
  header {
    backdrop-filter: blur(8px);
  }

  /* Añadir un efecto de subrayado al hover de los enlaces */
  nav ul li a {
    position: relative;
  }

  nav ul li a::after {
    content: '';
    position: absolute;
    width: 0;
    height: 2px;
    bottom: -4px;
    left: 0;
    background-color: #0d9488;
    transition: width 0.3s ease;
  }

  nav ul li a:hover::after {
    width: 100%;
  }

  /* Estilo para enlaces activos en el backoffice */
  .router-link-active {
    border-color: #0d9488;
    color: #0d9488;
  }

  .router-link-active::after {
    content: '';
    position: absolute;
    width: 100%;
    height: 2px;
    bottom: -4px;
    left: 0;
    background-color: #0d9488;
  }

  /* Estilos para los dropdowns */
  .dropdown-enter-active,
  .dropdown-leave-active {
    transition:
      opacity 0.2s,
      transform 0.2s;
  }

  .dropdown-enter-from,
  .dropdown-leave-to {
    opacity: 0;
    transform: translateY(-10px);
  }

  /* Estilo para el botón activo del dropdown */
  button.border-teal-500 svg:last-child {
    transform: rotate(180deg);
    transition: transform 0.2s;
  }

  /* Dropdown CSS-only para backoffice */
  .backoffice-dropdown {
    position: relative;
  }
  .backoffice-dropdown:focus-within .dropdown-menu,
  .backoffice-dropdown:hover .dropdown-menu {
    display: block;
    opacity: 1;
    pointer-events: auto;
  }
  .dropdown-menu {
    display: none;
    opacity: 0;
    pointer-events: none;
    position: absolute;
    left: 0;
    top: 100%;
    min-width: 14rem;
    margin-top: 0.5rem;
    background: white;
    border-radius: 0.375rem;
    box-shadow:
      0 10px 15px -3px rgba(0, 0, 0, 0.1),
      0 4px 6px -4px rgba(0, 0, 0, 0.1);
    z-index: 50;
    transition: opacity 0.2s;
  }
</style>
